/**
 * Authentication utilities for API requests
 */

export class AuthManager {
    static getToken() {
        return localStorage.getItem('authToken');
    }

    static getUser() {
        const userStr = localStorage.getItem('user');
        return userStr ? JSON.parse(userStr) : null;
    }

    static setAuth(token, user) {
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(user));
    }

    static clearAuth() {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
    }

    static isAuthenticated() {
        return !!this.getToken();
    }

    static async logout() {
        const user = this.getUser();
        const token = this.getToken();
        
        // For OIDC users, we'll do a local logout only (no TUI-wide logout)
        if (user && user.authMethod === 'oidc' && token) {
            try {
                // Call server logout endpoint to invalidate server-side session if needed
                await fetch('/api/auth/logout', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            } catch (error) {
                console.error('Server logout error:', error);
                // Continue with local logout even if server call fails
            }
        }
        
        // Always do local logout (clear tokens and redirect)
        this.clearAuth();
        window.location.href = '/login.html';
    }

    /**
     * Get headers with authentication token
     */
    static getAuthHeaders() {
        const token = this.getToken();
        const headers = {
            'Content-Type': 'application/json'
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        return headers;
    }

    /**
     * Fetch wrapper that includes authentication
     */
    static async fetch(url, options = {}) {
        const token = this.getToken();
        
        if (!token) {
            this.logout();
            throw new Error('Not authenticated');
        }

        // Add auth header
        const headers = {
            ...options.headers,
            'Authorization': `Bearer ${token}`
        };

        // Add Content-Type if body is present and not FormData
        if (options.body && !(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }

        const response = await fetch(url, {
            ...options,
            headers
        });

        // Handle 401 Unauthorized
        if (response.status === 401) {
            console.warn('Authentication failed, redirecting to login');
            this.logout();
            throw new Error('Authentication failed');
        }

        return response;
    }

    /**
     * Convenience methods for common HTTP methods
     */
    static async get(url) {
        return this.fetch(url, { method: 'GET' });
    }

    static async post(url, data) {
        return this.fetch(url, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    static async put(url, data) {
        return this.fetch(url, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    static async delete(url) {
        return this.fetch(url, { method: 'DELETE' });
    }
}

// Export as default for convenience
export default AuthManager;
